// src/components/ProgressTile/ProgressTile.jsx
import React from "react";
import PropTypes from "prop-types";
import "./progressTile.css";

const defaultFormatter = (n) =>
  typeof n === "number" ? n.toLocaleString("en-IN") : n;

/**
 * ProgressTile (self-contained)
 * Props:
 *  - data: {
 *      summary: { name: string, value: number, unit?: string },
 *      entries: Array<{ name: string, value: number, percentage: number, color?: string }>
 *    }
 *  - keyMap = { summary: string, entries: string }
 *  - columns?: number (default 2)
 *  - barHeight?: number (px, default 5)
 *  - trackColor?: string (default '#e6ebf3')
 *  - valueFormatter?: (number) => string (default en-IN locale)
 *  - className?: string
 */
export default function ProgressTile({
  data,
  keyMap = { summary: "summary", entries: "entries" },
  columns = 2,
  barHeight = 5,
  trackColor = "#e6ebf3",
  valueFormatter = defaultFormatter,
  className = "",
}) {
  const summary = data[keyMap.summary];
  const entries = data[keyMap.entries];

  if (!summary || !Array.isArray(entries)) return null;

  // Split entries evenly into columns, preserving order
  const perCol = Math.ceil(entries.length / columns);
  const cols = Array.from({ length: columns }, (_, i) =>
    entries.slice(i * perCol, (i + 1) * perCol),
  );

  const UNIT_MAP = {
    Tonnes: "T",
    tonnes: "T",
    Eaches: "E",
    eaches: "E",
    Cases: "Cs",
    cases: "Cs",
    Value: "Rs",
    value: "Rs",
    Rs: "Rs",
  };

  return (
    <div
      className={`progressTile ${className}`.trim()}
      style={{ "--ptile-cols": columns }} // lets CSS grid use the columns prop
    >
      <header className="progressHeader">
        <span className="progressTitle">{summary.name}</span>
        <span className="progressSeparator">&nbsp;:&nbsp;</span>
        <span className="progressValueHighlight">
          {typeof summary.value === "number"
            ? valueFormatter(summary.value)
            : summary.value}{" "}
        </span>
        <span className="metricUnit"> {UNIT_MAP[summary.unit]}</span>
      </header>

      <div className="progressGrid">
        {cols.map((col) => (
          <div
            className="progressCol"
            key={`col-${col[0]?.name || "empty"}-${col.at(-1)?.name || "end"}`}
          >
            {col.map((item) => {
              const isConnectedNode = item.name === "Connected Node";
              return (
                <ProgressRow
                  key={`${item.name}-${item.value}-${item.percentage}`}
                  label={item.name}
                  value={item.value}
                  percent={item.percentage}
                  color={isConnectedNode ? "#c2e0ff" : item.color}
                  barHeight={barHeight}
                  trackColor={trackColor}
                  valueFormatter={valueFormatter}
                  hidePercentage={isConnectedNode}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

ProgressTile.propTypes = {
  data: PropTypes.object.isRequired,
  keyMap: PropTypes.object,
  columns: PropTypes.number,
  barHeight: PropTypes.number,
  trackColor: PropTypes.string,
  valueFormatter: PropTypes.func,
  className: PropTypes.string,
};

/* ---------- Inline ProgressRow component ---------- */
/* ---------- Inline ProgressRow component ---------- */
function ProgressRow({
  label,
  value,
  percent,
  color = "#1f3fe4", // default blue
  trackColor = "#e6ebf3",
  barHeight = 5,
  valueFormatter = (n) => n,
  hidePercentage = false,
}) {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));

  // count digits only (ignore commas, spaces, symbols)
  const digitsOnly = String(value ?? "").replaceAll(/\D/g, "");
  const needEllipsis = digitsOnly.length > 4;

  // full text for tooltip (what user wants to see on hover)
  const fullText = valueFormatter(value);

  // display text: if >4 digits, trim to first 4 digits + …, else fullText
  let displayText = fullText;
  if (needEllipsis) {
    const head4 = digitsOnly.slice(0, 4);
    // preserve sign if present at the start of original string
    const sign = String(value ?? "")
      .trim()
      .startsWith("-")
      ? "-"
      : "";
    displayText = `${sign}${head4}…`;
  }

  return (
    <div className="progressRow">
      <div className="progressRowLabel" title={label}>
        {label}
      </div>

      <div className="progressBarWrap">
        <div
          className="progressBarTrack"
          style={{ backgroundColor: trackColor, height: barHeight }}
        >
          <progress
            value={pct}
            max={100}
            aria-label={label}
            aria-valuenow={pct}
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: "none",
            }}
          />
          <div
            className="progressBarFill"
            style={{
              width: `${pct}%`,
              backgroundColor: color,
              height: barHeight,
            }}
          />
        </div>
      </div>

      {/* value with ellipsis (4+ digits) and full on hover */}
      <div className="progressRowValue">
        <span
          className="progressRowValue valueEllipsis"
          data-full={fullText} // custom tooltip text (CSS ::after)
          title={fullText} // native tooltip (optional)
        >
          {displayText}
        </span>
      </div>

      {!hidePercentage && percent !== undefined && percent !== null && (
        <div className="progressRowPill">{pct} %</div>
      )}
    </div>
  );
}

ProgressRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string,
  trackColor: PropTypes.string,
  barHeight: PropTypes.number,
  valueFormatter: PropTypes.func,
  hidePercentage: PropTypes.bool,
};
